/**
 * strategyVersionDiff — structural diff between two strategy version snapshots
 * (Spec A / D51, AC-A6-3).
 *
 * Mirrors the intent of `CliFunnelDiffService.compare` (added / removed / changed
 * blocks + screens + variables, plus `changedScreenKeys`) but over two immutable
 * strategy version snapshots reconstructed by
 * `StrategyVersionService.getVersionSnapshot`. Pure + synchronous so it is unit
 * testable and renders in a read-only dialog without any network round-trip.
 *
 * Direction: base = the OLDER version, target = the NEWER version. "added" means
 * present in target but not base; "removed" means present in base but not target.
 */

import type { StrategyVersionSnapshot } from '@modules/firebase-core/services/StrategyVersionService';

export type VersionDiffAction = 'added' | 'removed' | 'changed' | 'unchanged';

export interface VersionScreenDiff {
  screenId: string;
  screenName?: string;
  action: VersionDiffAction;
}

export interface VersionBlockDiff {
  blockId: string;
  blockName?: string;
  action: VersionDiffAction;
  screens: VersionScreenDiff[];
  /** `${blockId}:${screenId}` for every screen that was added/removed/changed. */
  changedScreenKeys: string[];
}

export interface VersionVariableDiff {
  variableId: string;
  name?: string;
  action: VersionDiffAction;
}

export interface StrategyVersionDiff {
  blocks: VersionBlockDiff[];
  variables: VersionVariableDiff[];
  counts: { added: number; removed: number; changed: number; unchanged: number };
  changedScreenKeys: string[];
}

/** Deterministic JSON with recursively sorted keys — the equality basis. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

function withoutId<T extends { id?: unknown }>(value: T): Omit<T, 'id'> {
  const { id: _id, ...rest } = value;
  return rest;
}

function screenName(screen: Record<string, unknown>): string | undefined {
  const name = screen.name ?? screen.title ?? screen.screenName;
  return typeof name === 'string' ? name : undefined;
}

function blockName(block: Record<string, unknown>): string | undefined {
  return typeof block.name === 'string' ? block.name : undefined;
}

function diffScreens(
  blockId: string,
  baseScreens: Array<Record<string, unknown> & { id?: string }>,
  targetScreens: Array<Record<string, unknown> & { id?: string }>,
): { screens: VersionScreenDiff[]; changedScreenKeys: string[] } {
  const baseById = new Map(baseScreens.filter((s) => s.id).map((s) => [s.id as string, s]));
  const targetById = new Map(targetScreens.filter((s) => s.id).map((s) => [s.id as string, s]));
  const ids = Array.from(new Set([...baseById.keys(), ...targetById.keys()])).sort();

  const screens: VersionScreenDiff[] = [];
  const changedScreenKeys: string[] = [];
  for (const screenId of ids) {
    const base = baseById.get(screenId);
    const target = targetById.get(screenId);
    let action: VersionDiffAction;
    if (base && !target) action = 'removed';
    else if (!base && target) action = 'added';
    else if (stableStringify(withoutId(base!)) !== stableStringify(withoutId(target!))) action = 'changed';
    else action = 'unchanged';
    screens.push({ screenId, screenName: screenName((target ?? base) as Record<string, unknown>), action });
    if (action !== 'unchanged') changedScreenKeys.push(`${blockId}:${screenId}`);
  }
  return { screens, changedScreenKeys };
}

export function diffStrategyVersions(
  base: StrategyVersionSnapshot,
  target: StrategyVersionSnapshot,
): StrategyVersionDiff {
  const baseBlocks = new Map(base.blocks.map((b) => [b.id, b]));
  const targetBlocks = new Map(target.blocks.map((b) => [b.id, b]));
  const blockIds = Array.from(new Set([...baseBlocks.keys(), ...targetBlocks.keys()])).sort();

  const blocks: VersionBlockDiff[] = [];
  const allChangedScreenKeys: string[] = [];
  const counts = { added: 0, removed: 0, changed: 0, unchanged: 0 };

  for (const blockId of blockIds) {
    const baseBlock = baseBlocks.get(blockId);
    const targetBlock = targetBlocks.get(blockId);
    const { screens, changedScreenKeys } = diffScreens(
      blockId,
      (base.screensByBlock[blockId] ?? []) as unknown as Array<Record<string, unknown> & { id?: string }>,
      (target.screensByBlock[blockId] ?? []) as unknown as Array<Record<string, unknown> & { id?: string }>,
    );
    allChangedScreenKeys.push(...changedScreenKeys);

    let action: VersionDiffAction;
    if (baseBlock && !targetBlock) action = 'removed';
    else if (!baseBlock && targetBlock) action = 'added';
    else {
      const shellChanged =
        stableStringify(withoutId(baseBlock!)) !== stableStringify(withoutId(targetBlock!));
      action = shellChanged || changedScreenKeys.length > 0 ? 'changed' : 'unchanged';
    }
    counts[action] += 1;
    blocks.push({
      blockId,
      blockName: blockName((targetBlock ?? baseBlock) as Record<string, unknown>),
      action,
      screens,
      changedScreenKeys,
    });
  }

  // Variables live on the head-content blob (`head.variables`).
  const baseVars = (base.head as { variables?: Record<string, unknown> }).variables ?? {};
  const targetVars = (target.head as { variables?: Record<string, unknown> }).variables ?? {};
  const varIds = Array.from(new Set([...Object.keys(baseVars), ...Object.keys(targetVars)])).sort();
  const variables: VersionVariableDiff[] = [];
  for (const variableId of varIds) {
    const inBase = variableId in baseVars;
    const inTarget = variableId in targetVars;
    let action: VersionDiffAction;
    if (inBase && !inTarget) action = 'removed';
    else if (!inBase && inTarget) action = 'added';
    else if (stableStringify(baseVars[variableId]) !== stableStringify(targetVars[variableId])) action = 'changed';
    else action = 'unchanged';
    const def = (targetVars[variableId] ?? baseVars[variableId]) as { name?: string } | undefined;
    variables.push({ variableId, name: def?.name, action });
  }

  return {
    blocks,
    variables,
    counts,
    changedScreenKeys: allChangedScreenKeys,
  };
}
